import {Button, ButtonGroup, Stack, Typography, useTheme} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * MultipleChoiceIndividualSelectButton
 * Just one button that forms part of the MultipleChoiceButtonGroup
 *
 * @param {Object} props - Component props
 * @param {String} props.text - button title
 * @param {*} props.selected - the getter of a useState hook
 * @param {function} props.setSelected - the setter of a useState hook
 *
 * @returns {React.Component} Component
 */
const MultipleChoiceIndividualSelectButton = ({text, selected, setSelected, disabled}) => {
  const isGalaxyFold = useMediaQuery('(max-width:280px)');
  const theme = useTheme();

  return (
    <Button
      color={selected ? 'primary' : 'grayscale'}
      sx={{
        color: selected ? 'white' : 'black',
        '&.Mui-disabled': {
          color: selected ? 'black' : 'grayscale',
        },
        borderRadius: 5,
        fontWeight: 500,
        fontSize: isGalaxyFold ? 13 : 14,
      }}
      size="small"
      variant="contained"
      onClick={setSelected}
      disabled={disabled}
    >
      {text}
    </Button>
  );
};

/**
 * MultipleChoiceButtonGroup Component
 * Renders a multiple choice button where you can only choose one option and the value choose can be passed to a useState variable by using the selected and setSelected props. The options will always appear side by side forming a rounded rectangle
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Text that will appear above the button (optional)
 * @param {Array.<{
 *   text: string // date not including the year. Consists of 3 uppercase letters to represent the month and and the day of the month
 *   value: string // day of the week of that particular date
 * }>} props.choices - Array of objects containing the specific data that's going to be shown
 * @param {*} props.selected - useState getter for this component's value
 * @param {function} props.setSelected - useState setter for this component
 * @param {Array<Object> | Number | string} props.width - overriding width
 * @param {boolean} props.disabled - Whether the component can be interactable or not
 *
 * @returns {React.Component} Rendered component
 */
export const MultipleChoiceButtonGroup = ({title, choices, selected, setSelected, disabled, width, verticalMargin}) => (
  <Stack
    sx={{
      height: '100%',
      justifyContent: title === undefined ? 'center' : 'space-between',
    }}
  >
    <Typography
      sx={{
        fontWeight: 500,
        my: verticalMargin ?? 1,
        display: title === undefined ? 'none' : undefined,
      }}
    >
      {title}
    </Typography>
    <ButtonGroup
      fullWidth
      disabled={disabled}
      variant={selected != null ? 'text' : 'contained'}
      sx={{
        borderColor: 'black',
        width: width === undefined ? 1 : width,
        boxSizing: 'border-box',
        '& .MuiButtonGroup-grouped:not(:last-of-type)': {
          borderColor: '#D9D9D9',
        },
      }}
    >
      {choices.map((choice, index) => (
        <MultipleChoiceIndividualSelectButton
          text={choice.text}
          selected={selected === choice.value}
          setSelected={() => setSelected(choice.value)}
          key={index}
          disabled={disabled}
        />
      ))}
    </ButtonGroup>
  </Stack>
);
